import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { CONFIG } from '../config';

const { FiMenu, FiX, FiPhone, FiMail } = FiIcons;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    const sections = ['samples', 'stories', 'reviews', 'faq', 'quote'];
    sections.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const navLinks = [
    { name: "Demo Previews", href: "#samples", id: "samples" },
    { name: "Stories", href: "#stories", id: "stories" },
    { name: "Reviews", href: "#reviews", id: "reviews" },
    { name: "FAQ", href: "#faq", id: "faq" },
    { name: "Quote", href: "#quote", id: "quote" }
  ];

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 ${isScrolled ? 'py-4 bg-black/40 backdrop-blur-xl border-b border-white/5' : 'py-8 bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex flex-col cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <span className="text-white font-black text-xl tracking-tighter leading-none uppercase">
            {CONFIG.PRODUCT_NAME}
          </span>
          <span className="text-[9px] text-blue-500 font-bold tracking-[0.3em] uppercase mt-1">
            {CONFIG.BRAND_NAME}
          </span>
        </div>

        <nav className="hidden lg:flex items-center gap-10">
          <div className="flex items-center gap-8 border-r border-white/10 pr-10">
            {navLinks.map(link => (
              <button 
                key={link.name} 
                onClick={() => scrollTo(link.href)} 
                className={`text-[12px] font-black uppercase tracking-widest transition-all duration-300 relative group
                  ${activeSection === link.id ? 'text-blue-400' : 'text-slate-200 hover:text-white'}`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-blue-500 transition-all duration-300 
                  ${activeSection === link.id ? 'w-full' : 'w-0 group-hover:w-full'}`} 
                />
              </button>
            ))}
          </div>
          <div className="flex items-center gap-8">
            <a href={`tel:${CONFIG.SUPPORT_PHONE}`} className="flex items-center gap-3 group">
              <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 group-hover:bg-blue-500/20 transition-colors">
                <SafeIcon icon={FiPhone} className="text-blue-500 text-xs" />
              </div>
              <span className="text-[11px] font-bold text-slate-200 group-hover:text-white transition-colors">{CONFIG.SUPPORT_PHONE}</span>
            </a>
            <a href={`mailto:${CONFIG.SUPPORT_EMAIL}`} className="flex items-center gap-3 group">
              <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 group-hover:bg-blue-500/20 transition-colors">
                <SafeIcon icon={FiMail} className="text-blue-500 text-xs" />
              </div>
              <span className="text-[11px] font-bold text-slate-200 group-hover:text-white transition-colors">{CONFIG.SUPPORT_EMAIL}</span>
            </a>
          </div>
        </nav>

        <button className="lg:hidden p-3 rounded-xl glass border-white/10 text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="text-xl" />
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="absolute top-full left-0 right-0 bg-black/95 backdrop-blur-2xl border-b border-white/10 p-8 lg:hidden">
            <div className="flex flex-col gap-8 text-center">
              {navLinks.map(link => (
                <button 
                  key={link.name} 
                  onClick={() => scrollTo(link.href)} 
                  className={`text-sm font-black uppercase tracking-[0.2em] ${activeSection === link.id ? 'text-blue-400' : 'text-slate-300'}`}
                >
                  {link.name}
                </button>
              ))}
              <div className="h-px bg-white/5 w-1/4 mx-auto" />
              <div className="space-y-6">
                <a href={`tel:${CONFIG.SUPPORT_PHONE}`} className="flex items-center justify-center gap-4 text-sm text-white">
                  <SafeIcon icon={FiPhone} className="text-blue-500" /> {CONFIG.SUPPORT_PHONE}
                </a>
                <a href={`mailto:${CONFIG.SUPPORT_EMAIL}`} className="flex items-center justify-center gap-4 text-sm text-white">
                  <SafeIcon icon={FiMail} className="text-blue-500" /> {CONFIG.SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;